import React, { ChangeEvent } from 'react';
import { css } from 'styled-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import remove from 'ramda/src/remove';
import { Flex } from '../../../../../components/Flex';
import { ImagePreviews } from '../../../../../components/ImagePreviews';
import { MediaPicker } from '../../../../../components/MediaPicker';
import { TextArea } from '../../../../../components/TextArea';
import { Typography } from '../../../../../components/Typography';
import { emailSchema } from '../../../../../utils/validation-schemas/email';
import { getFormikFieldHelper } from '../../../../../utils/getFormikFieldHelper';
import { imagesSchema } from '../../../../../utils/validation-schemas/images';
import { tryCatch } from '../../../../../utils/tryCatch';
import { MaxNumberOfCharactersAccepted, MaxNumberOfPhotosAccepted, S3BucketDestination } from '../../../../../constants/app';
import { DataTestIds } from '../../../../../constants/data-testids';
import { RotateSwitchIcon } from '../../../../../icons/RotateSwitchIcon';
import { rotate } from '../../../../../keyframes/rotate';
import { uploadImages } from '../../../../../hooks/useUploadImages';
import { postDailyWagBlogArticleComment } from '../../../../../hooks/api/usePostDailyWagBlogArticleComment';
import { CamelizedArticleResponse } from '../../../../../types/api/article';
import { Grid } from '../../../../../wag-react/core-components/grid';
import { Box } from '../../../../../wag-react/next-components/box';
import { TextField } from '../../../../../wag-react/core-components/textfield';
import { Button } from '../../../../../wag-react/next-components/button';
enum FormFields {
  Name = 'name',
  Email = 'email',
  Comment = 'comment',
  Images = 'images',
}
const schema = Yup.object({
  [FormFields.Comment]: Yup.string().required().label('Comment').default('').max(MaxNumberOfCharactersAccepted.DailyWagArticleCommentForm),
  [FormFields.Email]: emailSchema.label('Email'),
  [FormFields.Images]: imagesSchema.notRequired().default([]),
  [FormFields.Name]: Yup.string().required().label('Your name').default('')
});
enum FormStatus {
  SubmittingComment = 'Submitting comment, please wait...',
  SubmittingPhotos = 'Submitting photos, please wait...',
  ErrorUploadingPhotos = 'Error uploading photos, please check your internet connection and try again',
}
export type DailyWagArticleCommentFormProps = {
  slug: string;
  onSubmitted?: (comment: CamelizedArticleResponse['commentList']) => void;
};
export const DailyWagArticleCommentForm = ({
  slug,
  onSubmitted = () => undefined
}: DailyWagArticleCommentFormProps) => {
  const dailyBlogCommentImageUploadFn = uploadImages(S3BucketDestination.DailyBlogComment);
  const {
    errors,
    getFieldHelpers,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    resetForm,
    setStatus,
    setSubmitting,
    setFieldError,
    status,
    touched,
    values
  } = useFormik({
    validateOnMount: false,
    initialValues: schema.cast(undefined),
    validationSchema: schema,
    onSubmit: async formValues => {
      const hasPhotosToUpload = !!formValues[FormFields.Images].length;
      setSubmitting(true);
      setStatus(hasPhotosToUpload ? FormStatus.SubmittingPhotos : FormStatus.SubmittingComment);
      const [uploadedImageUrls, uploadError] = await dailyBlogCommentImageUploadFn(formValues[FormFields.Images]);
      if (uploadError) {
        setFieldError(FormFields.Images, FormStatus.ErrorUploadingPhotos);
        return;
      }
      setStatus(FormStatus.SubmittingComment);
      const [response] = await tryCatch(() => postDailyWagBlogArticleComment(slug, {
        ...formValues,
        imageUrls: uploadedImageUrls,
        text: formValues[FormFields.Comment]
      }));

      // After all processing done
      setStatus(undefined);
      setSubmitting(false);
      resetForm();
      onSubmitted(response);
    }
  });
  const handleImagePreviewRemove = (index: number) => {
    // remove = you can think of this like .splice but in a functional way
    // no lists have been harmed. No mutating of the original array
    const files = remove(index, 1)(values[FormFields.Images]);
    getFieldHelpers(FormFields.Images).setValue(files);
  };
  const handleMediaPickerChange = (event: ChangeEvent<HTMLInputElement>) => {
    // If we want to reset the files on each upload,
    // do not accumulate
    const files = [...values[FormFields.Images], ...Array.from(event.target.files)];
    getFieldHelpers(FormFields.Images).setValue(files);
  };
  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched
  });
  return <form data-testid={DataTestIds.DailyWagArticle.CommentForm.Form} onSubmit={handleSubmit} data-sentry-component="DailyWagArticleCommentForm" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
      <Grid data-sentry-element="Grid" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
        <Grid gridTemplateColumns={['1fr', 'repeat(2, 1fr)']} gridGap="16px" data-sentry-element="Grid" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
          <Box data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
            <Typography mb="8px" data-sentry-element="Typography" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
              Your name
            </Typography>
            <TextField label="" variant="filled" data-testid={DataTestIds.DailyWagArticle.CommentForm.NameField} name={FormFields.Name} value={values[FormFields.Name]} error={formikFieldHelper(FormFields.Name).hasError} helperText={formikFieldHelper(FormFields.Name).errorMessage} onChange={handleChange} onBlur={handleBlur} fullWidth data-sentry-element="TextField" data-sentry-source-file="DailyWagArticleCommentForm.tsx" />
          </Box>
          <Box data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
            <Typography mb="8px" data-sentry-element="Typography" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
              Email
            </Typography>
            <TextField label="" variant="filled" data-testid={DataTestIds.DailyWagArticle.CommentForm.EmailField} type="email" name={FormFields.Email} value={values[FormFields.Email]} error={formikFieldHelper(FormFields.Email).hasError} helperText={formikFieldHelper(FormFields.Email).errorMessage} onChange={handleChange} onBlur={handleBlur} fullWidth data-sentry-element="TextField" data-sentry-source-file="DailyWagArticleCommentForm.tsx" />
          </Box>
        </Grid>
        <Box my="16px" data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
          <Typography mb="8px" data-sentry-element="Typography" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
            Comment
          </Typography>
          <TextArea label="" height={['174px', '104px', '96px']} data-testid={DataTestIds.DailyWagArticle.CommentForm.CommentField} name={FormFields.Comment} value={values[FormFields.Comment]} onChange={handleChange} onBlur={handleBlur} error={formikFieldHelper(FormFields.Comment).hasError} helperText={`${values[FormFields.Comment].length}/${MaxNumberOfCharactersAccepted.DailyWagArticleCommentForm}`} helperTextProps={{
          textAlign: 'right'
        }} {...formikFieldHelper(FormFields.Comment).hasError && {
          helperText: formikFieldHelper(FormFields.Comment).errorMessage,
          helperTextProps: {
            textAlign: 'unset'
          }
        }} data-sentry-element="TextArea" data-sentry-source-file="DailyWagArticleCommentForm.tsx" />
        </Box>

        <Box data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
          <Box my="16px" data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
            <ImagePreviews value={values[FormFields.Images]} onRemove={handleImagePreviewRemove} data-sentry-element="ImagePreviews" data-sentry-source-file="DailyWagArticleCommentForm.tsx" />
          </Box>
          <Box my="16px" display={values[FormFields.Images].length >= MaxNumberOfPhotosAccepted.DailyWagArticleCommentForm ? 'none' : 'block'} data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
            <MediaPicker name={FormFields.Images} max={MaxNumberOfPhotosAccepted.ExpertForm} onChange={handleMediaPickerChange} helperTextBlock={<>
                  <Typography variant="body2" color="wagGreen" fontWeight="bold" hover>
                    Add photo(s) of your pet
                  </Typography>
                  <Typography variant="body2" hover>
                    optional
                  </Typography>
                </>} data-sentry-element="MediaPicker" data-sentry-source-file="DailyWagArticleCommentForm.tsx" />
          </Box>
          {formikFieldHelper(FormFields.Images).hasError && <Typography variant="legal" color="red">
              {formikFieldHelper(FormFields.Images).errorMessage}
            </Typography>}
        </Box>
        <Box mt="40px" data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
          <Button variant="contained" type="submit" data-testid={DataTestIds.DailyWagArticle.CommentForm.SubmitBtn} disabled={!isValid || isSubmitting} data-sentry-element="Button" data-sentry-source-file="DailyWagArticleCommentForm.tsx">
            Submit comment
          </Button>
          {isSubmitting && <Flex alignItems="center" justifyContent="center" gridGap="8px">
              <RotateSwitchIcon css={css`
                  animation: ${rotate} 2s linear infinite;
                `} />
              <Typography variant="body2" fontWeight="bold" py="16px">
                {/* This will show text-switching while submitting */}
                {status}
              </Typography>
            </Flex>}
        </Box>
      </Grid>
    </form>;
};